import { Building2, Users, Trophy, Target, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react"
import { collection, query, orderBy, getDocs } from "firebase/firestore"
import { db } from "@/lib/firebase"

export default function AboutPage() {
  const [staff, setStaff] = useState<any[]>([])
  const [loadingStaff, setLoadingStaff] = useState(true)

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const q = query(collection(db, "staff"), orderBy("orderIndex", "asc"))
        const snap = await getDocs(q)
        const allStaff = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        setStaff(allStaff.filter((s: any) => s.isVisible !== false))
      } catch (error) {
        console.error("Error fetching staff:", error)
      } finally {
        setLoadingStaff(false)
      }
    }
    fetchStaff()
  }, [])

  return (
    <div className="bg-background min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-text-primary mb-4">About Rohith's Academy</h1>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Empowering aspirants to achieve their dreams in State Service Commissions through simplified learning and expert guidance.
          </p>
        </div>

        {/* Mission & Vision */}
        <section id="mission" className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
          <div className="bg-bg-secondary p-8 rounded-2xl border border-border">
            <div className="w-12 h-12 bg-primary/10 text-primary rounded-lg flex items-center justify-center mb-6">
              <Target size={24} />
            </div>
            <h2 className="text-2xl font-bold text-text-primary mb-4">Our Mission</h2>
            <p className="text-text-secondary leading-relaxed">
              To provide accessible, high-quality, and exam-oriented coaching to every aspirant in Andhra Pradesh and Telangana. We assist students in decoding the syllabus, mastering the concepts, and cracking the exam with confidence.
            </p>
          </div>
          <div className="bg-bg-secondary p-8 rounded-2xl border border-border">
            <div className="w-12 h-12 bg-primary/10 text-primary rounded-lg flex items-center justify-center mb-6">
              <Trophy size={24} />
            </div>
            <h2 className="text-2xl font-bold text-text-primary mb-4">Our Vision</h2>
            <p className="text-text-secondary leading-relaxed">
              To be the most trusted and result-oriented platform for competitive exams, fostering a community of dedicated civil servants who will serve the nation with integrity.
            </p>
          </div>
        </section>

        {/* Stats */}
        <section id="success-stories" className="bg-primary text-white rounded-2xl p-12 mb-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">15k+</div>
              <div className="text-primary-light">Students</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">500+</div>
              <div className="text-primary-light">Selections</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">50+</div>
              <div className="text-primary-light">Expert Faculty</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">4.9/5</div>
              <div className="text-primary-light">Rating</div>
            </div>
          </div>
        </section>

        {/* Faculty Preview */}
        <section id="faculty" className="mb-16">
          <div className="flex items-center gap-4 mb-8">
            <Users className="text-primary" size={32} />
            <h2 className="text-3xl font-bold text-text-primary">Meet Our Mentors</h2>
          </div>

          {loadingStaff ? (
            <div className="flex items-center justify-center py-12">
               <Loader2 className="animate-spin text-primary" size={24} />
            </div>
          ) : staff.length === 0 ? (
            <p className="text-text-secondary italic">Our faculty details are being updated.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {staff.map((member) => (
                <div key={member.id} className="bg-bg-secondary rounded-xl border border-border overflow-hidden group hover:shadow-xl transition-all">
                  <div className="h-64 bg-bg-hover w-full overflow-hidden">
                    {member.imageUrl ? (
                      <img src={member.imageUrl} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={member.name} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-text-muted">
                        <Users size={48} className="opacity-20" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-lg text-text-primary">{member.name}</h3>
                    <p className="text-primary text-sm font-medium mb-2">{member.subject}</p>
                    <p className="text-text-secondary text-sm line-clamp-3">
                      {member.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="text-center">
          <h3 className="text-2xl font-bold text-text-primary mb-4">Join the League of Officers</h3>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-primary text-white font-semibold hover:bg-primary-dark transition-smooth shadow-lg"
          >
            Start Preparation
          </Link>
        </div>
      </div>
    </div>
  );
}

